/**
 * TacticalMap.tsx  — orchestratore principale SI.TA.C.
 *
 * Responsabilità di questo file:
 *   - Stato applicativo (simboli, perimetro, vento, GPS, layer mappa)
 *   - Persistenza localStorage
 *   - Logica business (fetch vento, GPS survey, calcoli geometrici)
 *   - Composizione dei layer Leaflet e dei sotto-componenti UI
 *
 * UI delegata a:
 *   TacticalControls.tsx  → toolbar, banner, overlay perimetro, badge
 *   TacticalMapChrome.tsx → handler mappa, crosshair, griglia, preview, vettore vento
 *   WindAnalysisLayer.tsx → cono/triangolo vento
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  precacheTiles,
  getCacheInfo,
  clearTileCache,
  type TileCacheProgress,
} from '../../utils/tileCacheManager';
import html2canvas from 'html2canvas';
import { MapContainer } from 'react-leaflet';
import { SITACSymbolSelector, SITACSymbolWithCategory } from './SITACSymbolSelector';
import styles from './TacticalMap.module.css';
import LegendPanel from '../../map/layers/LegendOverlay';
import WindOverlay, {
  getWindDescription,
  getWindLabel,
  type WindDataSource,
  type WindFetchStatus,
} from '../../map/layers/WindOverlay';

import {
  generateSymbolId,
  exportSITACMap,
  type PlacedSymbol,
} from '@/config/sitac';

import { useMapStore } from '../../store/mapStore';
import { applyLeafletFix } from '../../map/utils/leafletFix';
import { createSITACIcon } from '../../map/utils/createSITACIcon';
import {
  calcolaAreaHa,
  calcolaPerimetroKm,
  esportaGeoJSON,
  puntoNelPoligono,
  type PerimetroMetadata,
} from '../../map/utils/perimetroUtils';

import BaseTileLayer from '../../map/layers/BaseTileLayer';
import { useUIStore } from '../../store/useUIStore';
import EFFISLayer, { EFFIS_LAYER_OPTIONS, type EFFISTemporalLayer, type EFFISStatus } from '../../map/layers/EFFISLayer';
import SITACSymbolsLayer from '../../map/layers/SITACSymbolsLayer';
import PerimetroIncendioLayer from '../../map/layers/PerimetroIncendioLayer';
import PosizioniCampoLayer from '../../map/layers/PosizioniCampoLayer';
import {
  CenterCrosshairOverlay,
  CenterSymbolPreview,
  GridOverlay,
  MapClickHandler,
  MapInstanceBridge,
  MapStateSync,
  MapViewUpdater,
  WindVectorOverlay,
} from './TacticalMapChrome';
import WindAnalysisLayer from './WindAnalysisLayer';
import { ElettrodottiLayer } from './ElettrodottiLayer';
import { useElettrodotti } from '../../hooks/useElettrodotti';
import VedetteLayer from '../../map/layers/VedetteLayer';
import MezziTerrestriLayer from '../../map/layers/MezziTerrestriLayer';
import DoSMarkerLayer from '../../map/layers/DoSMarkerLayer';
import { VedettaConeModal } from './VedettaConeModal';
import { useAppStore } from '../../store/useAppStore';
import { useCheckinMonitor } from '../../hooks/useCheckinMonitor';
import {
  computeActiveFrontSegment,
  computeLeftFlankSegment,
  computePolygonCentroid,
  computeRightFlankSegment,
  computeShelteredRearSegment,
  distanceMeters,
  getMinutesSince,
  getSegmentKey,
  getSitacStorageKey,
  normalizeDegrees,
  type TacticalMapSavedState,
} from './tacticalMapGeometry';
import {
  TacticalToolbar,
  PlacementBanner,
  PerimetroStatsOverlay,
  HillshadeBadge,
  EFFISLegendBadge,
  PerimetroModeBadge,
  InnescoModeBadge,
  type MapLayer,
  type PlacementMode,
} from './TacticalControls';

// ─────────────────────────────────────────────────────────────
// Tipi pubblici
// ─────────────────────────────────────────────────────────────

export interface GPSCoordinate {
  lat: number;
  lng: number;
}

interface TacticalMapProps {
  initialCenter?: GPSCoordinate;
  initialZoom?: number;
  initialSymbols?: PlacedSymbol[];
  onSymbolsChange?: (symbols: PlacedSymbol[]) => void;
  readOnly?: boolean;
  showGrid?: boolean;
  height?: string;
  eventId?: string;
  // Innesco automatico da geocoding indirizzo COAU
  initialInnesco?: [number, number] | null;
  // Ricerca/GPS integrata nella toolbar
  geoStatus?: string;
  onSearch?: () => void;
  isSearching?: boolean;
  onGPS?: () => void;
  resourceCounts?: { aerei: number; terrestri: number; simboli: number };
  codiceOperazione?: string;
  posizioniCampo?: import('../../integrations/supabase/types').PosizioneCampo[];
  campConnesso?: boolean;
  showPosizioniCampo?: boolean;
  onTogglePosizioniCampo?: () => void;
}

// ─────────────────────────────────────────────────────────────
// Helper: legge un campo tipizzato dal localStorage parsato
// ─────────────────────────────────────────────────────────────

function readStorage<T>(storageKey: string, reader: (p: TacticalMapSavedState) => T, fallback: T): T {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return fallback;
    return reader(JSON.parse(raw) as TacticalMapSavedState) ?? fallback;
  } catch {
    return fallback;
  }
}

// ─────────────────────────────────────────────────────────────
// Componente principale
// ─────────────────────────────────────────────────────────────

export const TacticalMap: React.FC<TacticalMapProps> = ({
  initialCenter = { lat: 44.7847, lng: 10.8791 },
  initialZoom = 14,
  initialSymbols = [],
  onSymbolsChange,
  readOnly = false,
  showGrid = true,
  height = '600px',
  eventId,
  initialInnesco,
  geoStatus,
  onSearch,
  isSearching,
  onGPS,
  resourceCounts,
  codiceOperazione,
  posizioniCampo = [],
  campConnesso = false,
  showPosizioniCampo: showPosizioniCampoProp = false,
  onTogglePosizioniCampo,
}) => {
  const storageKey = getSitacStorageKey(eventId);

  useEffect(() => { applyLeafletFix(); }, []);

  // ── Store mappa ──────────────────────────────────────────
  const setZoomStore   = useMapStore((s) => s.setZoom);
  const setCenterStore = useMapStore((s) => s.setCenter);
  const zoomStore      = useMapStore((s) => s.zoom);
  const centerStore    = useMapStore((s) => s.center);

  // ── Istanza Leaflet (catturata via MapInstanceBridge) ────
  const [mapInstance, setMapInstance] = useState<import('leaflet').Map | null>(null);

  // ── Layer elettrodotti ───────────────────────────────────
  const [elettrodottiVisible, setElettrodottiVisible] = useState(false);
  const {
    stato: statoElec,
    data:  dataElec,
    pericoloAereo,
    ricarica: ricaricaElec,
  } = useElettrodotti(mapInstance, elettrodottiVisible);

  // ── GPS survey ───────────────────────────────────────────
  const [gpsSurveyActive, setGpsSurveyActive] = useState(false);
  const gpsWatchRef      = useRef<number | null>(null);
  const lastPointRef     = useRef<[number, number] | null>(null);
  const importFileRef    = useRef<HTMLInputElement | null>(null);
  const mapAreaRef       = useRef<HTMLDivElement | null>(null);

  // ── Stato mappa caricato da storage ──────────────────────
  const [mapStateLoaded, setMapStateLoaded] = useState(false);

  // ── UI state ─────────────────────────────────────────────
  const [showMobileSymbols, setShowMobileSymbols] = useState(false);
  const [showSymbolSelector, setShowSymbolSelector] = useState(true);
  const toolbarTouchStartY = React.useRef(0);
  const { toolbarVisible, setToolbarVisible, openAllMenus, closeAllMenus } = useUIStore();
  const [mapLayer, setMapLayer] = useState<MapLayer>('street');
  // ── Tablet di campo ───────────────────────────────────────
  const [showPosizioniCampo, setShowPosizioniCampo] = useState(showPosizioniCampoProp);

  const [showEFFIS, setShowEFFIS] = useState(false);
  const [effisLayer, setEffisLayer] = useState<EFFISTemporalLayer>('modis.ba.season');
  const [effisStatus, setEffisStatus] = useState<EFFISStatus>('idle');
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedSymbolId, setSelectedSymbolId] = useState<string | null>(null);

  // ── Pannello download offline ─────────────────────────────
  const [showOfflinePanel, setShowOfflinePanel]     = useState(false);
  const [offlineLayer, setOfflineLayer]             = useState<string>('street');
  const [offlineRadius, setOfflineRadius]           = useState<number>(5);
  const [offlineZoomMax, setOfflineZoomMax]         = useState<number>(15);
  const [offlineProgress, setOfflineProgress]       = useState<TileCacheProgress | null>(null);
  const [isDownloading, setIsDownloading]           = useState(false);
  const [cacheInfo, setCacheInfo]                   = useState<{ tiles: number; sizeMB: number }>({ tiles: 0, sizeMB: 0 });
  const abortDownloadRef = useRef<AbortController | null>(null);

  // Refs per leggere i valori vento nel catch di fetchWindFromApi senza aggiungerli alle deps
  const windUpdatedAtRef   = useRef<string | null>(null);
  const windSourceLabelRef = useRef<string>('Operatore');
  const windDataSourceRef  = useRef<WindDataSource>('manual');

  const refreshCacheInfo = useCallback(async () => {
    const info = await getCacheInfo();
    setCacheInfo({ tiles: info.tiles, sizeMB: info.sizeMB });
  }, []);

  useEffect(() => {
    if (showOfflinePanel) refreshCacheInfo();
  }, [showOfflinePanel, refreshCacheInfo]);

  const handleStartDownload = useCallback(async () => {
    const center = mapInstance?.getCenter();
    if (!center) { alert('Centra la mappa sull\'area da scaricare prima di procedere.'); return; }
    const ctrl = new AbortController();
    abortDownloadRef.current = ctrl;
    setIsDownloading(true);
    setOfflineProgress(null);
    try {
      await precacheTiles(
        center.lat, center.lng,
        offlineRadius, 10, offlineZoomMax,
        (p) => setOfflineProgress(p),
        ctrl.signal,
        undefined,
        offlineLayer,
      );
    } finally {
      setIsDownloading(false);
      abortDownloadRef.current = null;
      refreshCacheInfo();
    }
  }, [mapInstance, offlineLayer, offlineRadius, offlineZoomMax, refreshCacheInfo]);

  const handleCancelDownload = useCallback(() => {
    abortDownloadRef.current?.abort();
  }, []);

  const handleClearCache = useCallback(async () => {
    if (!confirm('Svuotare tutta la cache mappe offline?')) return;
    await clearTileCache();
    refreshCacheInfo();
    setOfflineProgress(null);
  }, [refreshCacheInfo]);

  // ── Simboli ──────────────────────────────────────────────
  const [placedSymbols, setPlacedSymbols] = useState<PlacedSymbol[]>(() =>
    readStorage(storageKey, (p) =>
      (p.placedSymbols ?? []).map((s) => ({ ...s, timestamp: new Date(s.timestamp) })),
      initialSymbols
    )
  );

  // ── Placement mode ───────────────────────────────────────
  const [placementMode, setPlacementMode] = useState<PlacementMode>({ active: false, symbol: null });

  // ── Perimetro ────────────────────────────────────────────
  const [perimetroMode, setPerimetroMode]     = useState(false);
  const [showPerimetroStats, setShowPerimetroStats] = useState(false);
  const [puntiPerimetro, setPuntiPerimetro]   = useState<[number, number][]>(() =>
    readStorage(storageKey, (p) => p.puntiPerimetro ?? [], [])
  );
  const [perimetroChiuso, setPerimetroChiuso] = useState<boolean>(() =>
    readStorage(storageKey, (p) => p.perimetroChiuso ?? false, false)
  );
  const [pendingVertexIndex, setPendingVertexIndex] = useState<number | null>(null);

  const [showLegend, setShowLegend] = useState(false);

  // ── Punto d'innesco ──────────────────────────────────────
  const [puntoInnesco, setPuntoInnesco] = useState<[number, number] | null>(() =>
    readStorage(storageKey, (p) => p.puntoInnesco ?? null, null)
  );
  const [innescoMode, setInnescoMode] = useState(false);

  // ── Vedette ──────────────────────────────────────────────────
  const lookouts      = useAppStore((s) => s.lookouts);
  const updateLookout = useAppStore((s) => s.updateLookout);
  const [vedettaMode, setVedettaMode]             = useState(false);
  const [vedettaToPlaceId, setVedettaToPlaceId]   = useState<number | null>(null);
  const [vedettaConeSettingsId, setVedettaConeSettingsId] = useState<number | null>(null);
  const { overdueIds } = useCheckinMonitor();

  // ── Mezzi terrestri ──────────────────────────────────────────
  const mezziTerrestri        = useAppStore((s) => s.mezziTerrestri);
  const updateMezzoTerrestre  = useAppStore((s) => s.updateMezzoTerrestre);
  const [mezzoTerrestreMode, setMezzoTerrestreMode]         = useState(false);
  const [mezzoTerrestreToPlaceId, setMezzoTerrestreToPlaceId] = useState<string | null>(null);

  // ── Posizione DOS ─────────────────────────────────────────────
  const coordinate            = useAppStore((s) => s.coordinate);
  const aggiornaPosizioneDoS  = useAppStore((s) => s.aggiornaPosizioneDoS);
  const [dosGpsLoading, setDosGpsLoading] = useState(false);
  const posizioneDoS = coordinate.find(
    (c) => (c as any).titolo === 'Posizione DOS' || c.name === 'Posizione DOS'
  );
  const dosLat = posizioneDoS ? parseFloat(String(posizioneDoS.lat)) : null;
  const dosLng = posizioneDoS ? parseFloat(String((posizioneDoS as any).lon ?? (posizioneDoS as any).lng)) : null;

  // Sincronizza il punto d'innesco con le coordinate COAU (geocoding o GPS).
  // Sovrascrive sempre quando initialInnesco cambia: l'indirizzo COAU è la fonte.
  useEffect(() => {
    if (!initialInnesco) return;
    setPuntoInnesco(initialInnesco);
  }, [initialInnesco]);

  // ── Metadata perimetro ───────────────────────────────────
  const [tipoVegetazione, setTipoVegetazione] = useState<string>(() =>
    readStorage(storageKey, (p) => p.tipoVegetazione ?? '', '')
  );
  const [noteOperative, setNoteOperative] = useState<string>(() =>
    readStorage(storageKey, (p) => p.noteOperative ?? '', '')
  );

  // ── Vento ─────────────────────────────────────────────────
  const [windDirectionDeg, setWindDirectionDeg]       = useState<number>(() => readStorage(storageKey, (p) => p.windDirectionDeg, 225));
  const [windSpeedKmh, setWindSpeedKmh]               = useState<number>(() => readStorage(storageKey, (p) => p.windSpeedKmh, 18));
  const [windDataSource, setWindDataSource]           = useState<WindDataSource>(() => readStorage(storageKey, (p) => p.windDataSource ?? 'manual', 'manual'));
  const [windUpdatedAt, setWindUpdatedAt]             = useState<string | null>(() => readStorage(storageKey, (p) => p.windUpdatedAt ?? null, null));
  const [windSourceLabel, setWindSourceLabel]         = useState<string>(() => readStorage(storageKey, (p) => p.windSourceLabel ?? 'Operatore', 'Operatore'));
  const [windFetchStatus, setWindFetchStatus]         = useState<WindFetchStatus>('idle');
  const [windRefreshIntervalMin, setWindRefreshIntervalMin] = useState<number>(() => readStorage(storageKey, (p) => p.windRefreshIntervalMin ?? 10, 10));
  const [windStaleAfterMin, setWindStaleAfterMin]     = useState<number>(() => readStorage(storageKey, (p) => p.windStaleAfterMin ?? 30, 30));
  // triangolo propagazione: automatico, non serve stato visibilità

  // ─────────────────────────────────────────────────────────
  // Persistenza localStorage
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!mapStateLoaded) return;
    try {
      const dataToSave: TacticalMapSavedState = {
        placedSymbols,
        puntiPerimetro,
        perimetroChiuso,
        mapCenter: centerStore,
        mapZoom: zoomStore,
        windDirectionDeg,
        windSpeedKmh,
        windDataSource,
        windUpdatedAt,
        windSourceLabel,
        windRefreshIntervalMin,
        windStaleAfterMin,
        puntoInnesco,
        tipoVegetazione,
        noteOperative,
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Errore salvataggio SITAC:', error);
    }
  }, [
    mapStateLoaded, placedSymbols, puntiPerimetro, perimetroChiuso,
    centerStore, zoomStore, windDirectionDeg, windSpeedKmh,
    windDataSource, windUpdatedAt, windSourceLabel,
    windRefreshIntervalMin, windStaleAfterMin, puntoInnesco,
    tipoVegetazione, noteOperative, storageKey,
  ]);

  // ─────────────────────────────────────────────────────────
  // Ripristino centro/zoom iniziale
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    try {
      const raw   = localStorage.getItem(storageKey);
      const parsed: TacticalMapSavedState | null = raw ? JSON.parse(raw) : null;
      const hasValidCenter =
        typeof initialCenter?.lat === 'number' &&
        typeof initialCenter?.lng === 'number' &&
        !Number.isNaN(initialCenter.lat) &&
        !Number.isNaN(initialCenter.lng);

      if (hasValidCenter) {
        setCenterStore([initialCenter.lat, initialCenter.lng]);
        setZoomStore(initialZoom);
      } else if (parsed?.mapCenter) {
        setCenterStore(parsed.mapCenter);
        setZoomStore(typeof parsed.mapZoom === 'number' ? parsed.mapZoom : initialZoom);
      } else {
        setCenterStore([44.7847, 10.8791]);
        setZoomStore(initialZoom);
      }
    } catch {
      setCenterStore([initialCenter.lat, initialCenter.lng]);
      setZoomStore(initialZoom);
    }
    setMapStateLoaded(true);
  }, [initialCenter.lat, initialCenter.lng, initialZoom, setCenterStore, setZoomStore, storageKey]);

  // ─────────────────────────────────────────────────────────
  // Calcoli derivati (memoizzati)
  // ─────────────────────────────────────────────────────────

  const layoutVersion = [
    showSymbolSelector ? 1 : 0,
    showMobileSymbols  ? 1 : 0,
    selectedSymbolId   ? 1 : 0,
    perimetroMode      ? 1 : 0,
  ].reduce((acc, n) => acc * 10 + n, 0);

  const areaHa      = calcolaAreaHa(puntiPerimetro);
  const perimetroKm = calcolaPerimetroKm(
    perimetroChiuso && puntiPerimetro.length >= 3
      ? [...puntiPerimetro, puntiPerimetro[0]]
      : puntiPerimetro
  );

  const windDescription  = useMemo(() => getWindDescription(windSpeedKmh),               [windSpeedKmh]);
  const propagationDeg   = useMemo(() => normalizeDegrees(windDirectionDeg + 180),        [windDirectionDeg]);
  const propagationLabel = useMemo(() => getWindLabel(propagationDeg),                    [propagationDeg]);
  const windAgeMinutes   = useMemo(() => getMinutesSince(windUpdatedAt),                  [windUpdatedAt]);
  const windDataStale    = useMemo(
    () => windDataSource !== 'manual' && windAgeMinutes !== null && windAgeMinutes > windStaleAfterMin,
    [windDataSource, windAgeMinutes, windStaleAfterMin]
  );

  // I segmenti fronte/fianchi/retro rimangono per la colorazione del perimetro
  // ma non servono più per il triangolo di propagazione
  const activeFrontSegment = useMemo(
    () => computeActiveFrontSegment(puntiPerimetro, perimetroChiuso, propagationDeg),
    [puntiPerimetro, perimetroChiuso, propagationDeg]
  );
  const shelteredRearSegment = useMemo(
    () => computeShelteredRearSegment(puntiPerimetro, perimetroChiuso, propagationDeg),
    [puntiPerimetro, perimetroChiuso, propagationDeg]
  );
  const excludedMainSegmentKeys = useMemo(() => {
    const keys = new Set<string>();
    const frontKey = getSegmentKey(activeFrontSegment);
    const rearKey  = getSegmentKey(shelteredRearSegment);
    if (frontKey) keys.add(frontKey);
    if (rearKey)  keys.add(rearKey);
    return keys;
  }, [activeFrontSegment, shelteredRearSegment]);
  const rightFlankSegment = useMemo(
    () => computeRightFlankSegment(puntiPerimetro, perimetroChiuso, propagationDeg, excludedMainSegmentKeys),
    [puntiPerimetro, perimetroChiuso, propagationDeg, excludedMainSegmentKeys]
  );
  const leftFlankSegment = useMemo(
    () => computeLeftFlankSegment(puntiPerimetro, perimetroChiuso, propagationDeg, excludedMainSegmentKeys),
    [puntiPerimetro, perimetroChiuso, propagationDeg, excludedMainSegmentKeys]
  );

  // Distanza dal punto d'innesco al midpoint del segmento di fronte attivo
  const distanzaInnescoFronte = useMemo<number | null>(() => {
    if (!puntoInnesco || !activeFrontSegment) return null;
    const mid: [number, number] = [
      (activeFrontSegment[0][0] + activeFrontSegment[1][0]) / 2,
      (activeFrontSegment[0][1] + activeFrontSegment[1][1]) / 2,
    ];
    return distanceMeters(puntoInnesco, mid);
  }, [puntoInnesco, activeFrontSegment]);

  // True se il perimetro è chiuso e l'innesco non è contenuto nel poligono
  const innescoFuoriPerimetro = useMemo<boolean>(() => {
    if (!puntoInnesco || !perimetroChiuso || puntiPerimetro.length < 3) return false;
    return !puntoNelPoligono(puntoInnesco, puntiPerimetro);
  }, [puntoInnesco, perimetroChiuso, puntiPerimetro]);

  const selectedSymbol = placedSymbols.find((s) => s.id === selectedSymbolId);

  const activeMapBorder = innescoMode
    ? '3px solid #dc2626'
    : vedettaMode
      ? '3px solid #059669'
      : mezzoTerrestreMode
        ? '3px solid #b45309'
        : perimetroMode
          ? '3px solid #ff4500'
          : placementMode.active
            ? '3px solid #3b82f6'
            : '1px solid transparent';
  const activeMapShadow = innescoMode
    ? '0 0 0 2px rgba(220,38,38,0.28)'
    : vedettaMode
      ? '0 0 0 2px rgba(5,150,105,0.28)'
      : mezzoTerrestreMode
        ? '0 0 0 2px rgba(180,83,9,0.28)'
        : perimetroMode
          ? '0 0 0 2px rgba(255,69,0,0.25)'
          : placementMode.active
            ? '0 0 0 2px rgba(59,130,246,0.25)'
            : 'none';

  // ─────────────────────────────────────────────────────────
  // Handler vento
  // ─────────────────────────────────────────────────────────

  const resolveWindAnchor = useCallback((): [number, number] => {
    return computePolygonCentroid(puntiPerimetro) ?? centerStore;
  }, [puntiPerimetro, centerStore]);

  const applyWindReading = useCallback(
    (payload: { directionDeg: number; speedKmh: number; updatedAt?: string | null }, source: WindDataSource, sourceName: string) => {
      setWindDirectionDeg(normalizeDegrees(payload.directionDeg));
      setWindSpeedKmh(Math.max(0, payload.speedKmh));
      setWindDataSource(source);
      setWindUpdatedAt(payload.updatedAt ?? new Date().toISOString());
      setWindSourceLabel(sourceName);
      setWindFetchStatus(source === 'manual' ? 'idle' : 'ok');
    },
    []
  );

  // Mantieni i ref sincronizzati con lo stato vento (per il catch di fetchWindFromApi)
  windUpdatedAtRef.current   = windUpdatedAt;
  windSourceLabelRef.current = windSourceLabel;
  windDataSourceRef.current  = windDataSource;

  const fetchWindFromApi = useCallback(async () => {
    const [lat, lng] = resolveWindAnchor();
    setWindFetchStatus('loading');
    try {
      const controller = new AbortController();
      const timeout    = window.setTimeout(() => controller.abort(), 5000);
      const url        = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=wind_speed_10m,wind_direction_10m`;
      const response   = await fetch(url, { signal: controller.signal });
      window.clearTimeout(timeout);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data      = await response.json();
      const speed     = Number(data?.current?.wind_speed_10m);
      const direction = Number(data?.current?.wind_direction_10m);

      if (!Number.isFinite(speed) || !Number.isFinite(direction)) throw new Error('Dati vento non validi');

      applyWindReading(
        { directionDeg: direction, speedKmh: speed, updatedAt: typeof data?.current?.time === 'string' ? data.current.time : new Date().toISOString() },
        'auto',
        'Open-Meteo'
      );
    } catch {
      if (windUpdatedAtRef.current && windSourceLabelRef.current && windDataSourceRef.current !== 'manual') {
        setWindDataSource('cached');
        setWindFetchStatus('error');
      } else {
        setWindDataSource('manual');
        setWindSourceLabel('Operatore');
        setWindFetchStatus('error');
      }
    }
  }, [applyWindReading, resolveWindAnchor]);

  const handleWindSourceChange = useCallback(
    (source: WindDataSource) => {
      if (source === 'manual') {
        setWindDataSource('manual');
        setWindSourceLabel('Operatore');
        setWindFetchStatus('idle');
        if (!windUpdatedAt) setWindUpdatedAt(new Date().toISOString());
        return;
      }
      setWindDataSource('auto');
      void fetchWindFromApi();
    },
    [fetchWindFromApi, windUpdatedAt]
  );

  useEffect(() => {
    if (!mapStateLoaded || readOnly || windDataSource !== 'auto' || windRefreshIntervalMin <= 0) return;
    const id = window.setInterval(() => void fetchWindFromApi(), windRefreshIntervalMin * 60 * 1000);
    return () => window.clearInterval(id);
  }, [mapStateLoaded, readOnly, windDataSource, windRefreshIntervalMin, fetchWindFromApi]);

  useEffect(() => {
    if (!mapStateLoaded || readOnly || windDataSource !== 'auto') return;
    void fetchWindFromApi();
  }, [mapStateLoaded, readOnly, windDataSource, fetchWindFromApi]);

  // ─────────────────────────────────────────────────────────
  // Handler simboli
  // ─────────────────────────────────────────────────────────

  const handleSymbolSelect = useCallback((symbol: SITACSymbolWithCategory) => {
    setPerimetroMode(false);
    setPlacementMode({ active: true, symbol });
  }, []);

  const handlePlaceSymbol = useCallback((lat: number, lng: number) => {
    if (!placementMode.active || !placementMode.symbol) return;
    const newSymbol: PlacedSymbol = {
      id: generateSymbolId(),
      symbol: placementMode.symbol,
      position: { lat, lng },
      rotation: 0,
      scale: 1,
      timestamp: new Date(),
    };
    const updated = [...placedSymbols, newSymbol];
    setPlacedSymbols(updated);
    setPlacementMode({ active: false, symbol: null });
    setSelectedSymbolId(newSymbol.id);
    onSymbolsChange?.(updated);
  }, [placementMode, placedSymbols, onSymbolsChange]);

  const handleRotationChange = useCallback((symbolId: string, rotation: number) => {
    const updated = placedSymbols.map((s) => s.id === symbolId ? { ...s, rotation } : s);
    setPlacedSymbols(updated);
    onSymbolsChange?.(updated);
  }, [placedSymbols, onSymbolsChange]);

  const handleMoveSymbol = useCallback((symbolId: string, lat: number, lng: number) => {
    const updated = placedSymbols.map((s) => s.id === symbolId ? { ...s, position: { lat, lng } } : s);
    setPlacedSymbols(updated);
    onSymbolsChange?.(updated);
  }, [placedSymbols, onSymbolsChange]);

  const handleScaleChange = useCallback((symbolId: string, scale: number) => {
    const updated = placedSymbols.map((s) => s.id === symbolId ? { ...s, scale } : s);
    setPlacedSymbols(updated);
    onSymbolsChange?.(updated);
  }, [placedSymbols, onSymbolsChange]);

  const handleDeleteSymbol = useCallback((symbolId: string) => {
    const updated = placedSymbols.filter((s) => s.id !== symbolId);
    setPlacedSymbols(updated);
    if (selectedSymbolId === symbolId) setSelectedSymbolId(null);
    onSymbolsChange?.(updated);
  }, [placedSymbols, selectedSymbolId, onSymbolsChange]);

  const handleExportMap = useCallback(() => {
    const mapData = exportSITACMap(placedSymbols, { operatore: 'Angelo - DOS', location: 'Incidente' });
    const blob    = new Blob([JSON.stringify(mapData, null, 2)], { type: 'application/json' });
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement('a');
    a.href        = url;
    a.download    = `sitac_map_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [placedSymbols]);

  const handleClearMap = useCallback(() => {
    if (window.confirm('Rimuovere tutti i simboli dalla mappa?')) {
      setPlacedSymbols([]);
      setSelectedSymbolId(null);
      onSymbolsChange?.([]);
    }
  }, [onSymbolsChange]);

  // ─────────────────────────────────────────────────────────
  // Handler perimetro
  // ─────────────────────────────────────────────────────────

  const handleDeleteVertex = useCallback((index: number) => {
    setPuntiPerimetro((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (updated.length < 3) setPerimetroChiuso(false);
      return updated;
    });
    setPendingVertexIndex(null);
  }, []);

  const handleImportGeoJSON = useCallback(() => {
    importFileRef.current?.click();
  }, []);

  const handleImportFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string);
        let coords: number[][] | null = null;

        // Estrae le coordinate dal GeoJSON (FeatureCollection, Feature, Polygon, LineString)
        const extractCoords = (geom: { type: string; coordinates: number[][][] | number[][] }) => {
          if (geom.type === 'Polygon')    return geom.coordinates[0] as number[][];
          if (geom.type === 'LineString') return geom.coordinates as number[][];
          return null;
        };

        if (json.type === 'FeatureCollection' && Array.isArray(json.features)) {
          const feat = json.features.find((f: { geometry: { type: string } }) =>
            f.geometry?.type === 'Polygon' || f.geometry?.type === 'LineString'
          );
          if (feat) coords = extractCoords(feat.geometry);
        } else if (json.type === 'Feature') {
          coords = extractCoords(json.geometry);
        } else {
          coords = extractCoords(json);
        }

        if (!coords || coords.length < 3) {
          alert('GeoJSON non valido o insufficiente (servono almeno 3 punti).');
          return;
        }

        // GeoJSON standard: [lon, lat] → Leaflet [lat, lon].
        // Se il file usa già [lat, lon] (non standard), le coordinate invertite
        // escono fuori dall'Italia: in quel caso le usiamo senza inversione.
        const isInItalia = (lat: number, lng: number) =>
          lat >= 34 && lat <= 48 && lng >= 5 && lng <= 20;

        const puntiStd: [number, number][]     = coords.map(([lon, lat]) => [lat, lon]);
        const puntiDiretti: [number, number][] = coords.map(([lat, lon]) => [lat, lon]);

        let punti: [number, number][];
        if (puntiStd.every(([lat, lng]) => isInItalia(lat, lng))) {
          punti = puntiStd;
        } else if (puntiDiretti.every(([lat, lng]) => isInItalia(lat, lng))) {
          punti = puntiDiretti;
        } else {
          alert(
            'Coordinate non valide: nessun punto ricade in territorio italiano.\n' +
            'Verifica che il file GeoJSON usi coordinate WGS84 (EPSG:4326).'
          );
          return;
        }
        setPuntiPerimetro(punti);
        setPerimetroChiuso(true);
        setPerimetroMode(false);
        setShowPerimetroStats(true);
        setShowSymbolSelector(true);

        // Centra la mappa sul baricentro del perimetro importato
        const centroid = punti.reduce(
          ([la, lo], [a, o]) => [la + a / punti.length, lo + o / punti.length],
          [0, 0]
        ) as [number, number];
        setCenterStore(centroid);
        setZoomStore(14);
      } catch {
        alert('Errore nella lettura del file GeoJSON.');
      }
    };
    reader.readAsText(file);
    // Reset input per permettere di reimportare lo stesso file
    e.target.value = '';
  }, [setCenterStore, setZoomStore]);

  const handleInsertVertex = useCallback((index: number, lat: number, lng: number) => {
    setPuntiPerimetro((prev) => {
      const updated = [...prev];
      updated.splice(index, 0, [lat, lng]);
      return updated;
    });
    setPendingVertexIndex(index);
  }, []);

  const handleMoveVertex = useCallback((index: number, lat: number, lng: number) => {
    setPuntiPerimetro((prev) => {
      const updated  = [...prev];
      updated[index] = [lat, lng];
      return updated;
    });
    setPendingVertexIndex((current) => (current === index ? null : current));
  }, []);

  const handleAddPuntoPerimetro = useCallback((lat: number, lng: number) => {
    if (pendingVertexIndex !== null) { handleMoveVertex(pendingVertexIndex, lat, lng); return; }
    if (perimetroChiuso) return;
    setPuntiPerimetro((p) => [...p, [lat, lng]]);
  }, [perimetroChiuso, pendingVertexIndex, handleMoveVertex]);

  const handleChiudiPerimetro = useCallback(() => {
    if (puntiPerimetro.length >= 3) {
      setPerimetroChiuso(true);
      setPerimetroMode(false);
      setShowSymbolSelector(true);
    }
  }, [puntiPerimetro.length]);

  const handleResetPerimetro = useCallback(() => {
    if (gpsWatchRef.current !== null) { navigator.geolocation.clearWatch(gpsWatchRef.current); gpsWatchRef.current = null; }
    setGpsSurveyActive(false);
    lastPointRef.current = null;
    setPendingVertexIndex(null);
    setPuntiPerimetro([]);
    setPerimetroChiuso(false);
    setPerimetroMode(false);
    setShowPerimetroStats(false);
    setShowSymbolSelector(true);
    setTipoVegetazione('');
    setNoteOperative('');
  }, []);

  const handleExportPerimetro = useCallback(() => {
    if (puntiPerimetro.length >= 3) {
      const meta: PerimetroMetadata = {
        tipoVegetazione: tipoVegetazione || undefined,
        noteOperative: noteOperative || undefined,
        innesco: puntoInnesco,
      };
      esportaGeoJSON(puntiPerimetro, areaHa, meta);
    }
  }, [puntiPerimetro, areaHa, tipoVegetazione, noteOperative, puntoInnesco]);

  const handleTogglePerimetroMode = useCallback(() => {
    setShowMobileSymbols(false);
    if (perimetroMode) {
      setPerimetroMode(false);
      setShowSymbolSelector(true);
    } else {
      setPerimetroMode(true);
      setShowPerimetroStats(true);
      setPlacementMode({ active: false, symbol: null });
      setShowSymbolSelector(false);
    }
  }, [perimetroMode]);

  const handleModificaPerimetro = useCallback(() => {
    setPerimetroMode(true);
    setShowSymbolSelector(false);
    setShowMobileSymbols(false);
    setPlacementMode({ active: false, symbol: null });
  }, []);

  // ─────────────────────────────────────────────────────────
  // Handler punto d'innesco
  // ─────────────────────────────────────────────────────────

  const handleToggleInnescoMode = useCallback(() => {
    if (innescoMode) {
      setInnescoMode(false);
    } else {
      setInnescoMode(true);
      setPerimetroMode(false);
      setPlacementMode({ active: false, symbol: null });
      setShowSymbolSelector(false);
      setShowMobileSymbols(false);
    }
  }, [innescoMode]);

  const handleSetInnesco = useCallback((lat: number, lng: number) => {
    setPuntoInnesco([lat, lng]);
    setInnescoMode(false);
    setShowSymbolSelector(true);
  }, []);

  const handleMoveInnesco = useCallback((lat: number, lng: number) => {
    setPuntoInnesco([lat, lng]);
  }, []);

  const handleResetInnesco = useCallback(() => {
    if (window.confirm('Rimuovere il punto d\'innesco?')) {
      setPuntoInnesco(null);
    }
  }, []);

  // ─────────────────────────────────────────────────────────
  // Handler vedette
  // ─────────────────────────────────────────────────────────

  const handleToggleVedettaMode = useCallback(() => {
    if (vedettaMode) {
      setVedettaMode(false);
      setVedettaToPlaceId(null);
    } else {
      setVedettaMode(true);
      setInnescoMode(false);
      setPerimetroMode(false);
      setPlacementMode({ active: false, symbol: null });
      setShowSymbolSelector(false);
      setShowMobileSymbols(false);
    }
  }, [vedettaMode]);

  const handleSelectVedettaToPlace = useCallback((id: number) => {
    setVedettaToPlaceId(id);
  }, []);

  const handleSetVedettaPosition = useCallback((lat: number, lng: number) => {
    if (vedettaToPlaceId === null) return;
    updateLookout(vedettaToPlaceId, { lat, lng });
    setVedettaToPlaceId(null);
    setVedettaMode(false);
    setShowSymbolSelector(true);
  }, [vedettaToPlaceId, updateLookout]);

  const handleMoveVedetta = useCallback((id: number, lat: number, lng: number) => {
    updateLookout(id, { lat, lng });
  }, [updateLookout]);

  // ─────────────────────────────────────────────────────────
  // Handler mezzi terrestri
  // ─────────────────────────────────────────────────────────

  const handleToggleMezzoTerrestreMode = useCallback(() => {
    if (mezzoTerrestreMode) {
      setMezzoTerrestreMode(false);
      setMezzoTerrestreToPlaceId(null);
    } else {
      setMezzoTerrestreMode(true);
      setVedettaMode(false);
      setVedettaToPlaceId(null);
      setInnescoMode(false);
      setPerimetroMode(false);
      setPlacementMode({ active: false, symbol: null });
      setShowSymbolSelector(false);
      setShowMobileSymbols(false);
    }
  }, [mezzoTerrestreMode]);

  const handleSelectMezzoToPlace = useCallback((id: string) => {
    setMezzoTerrestreToPlaceId(id);
  }, []);

  const handleSetMezzoTerrestrePosition = useCallback((lat: number, lng: number) => {
    if (mezzoTerrestreToPlaceId === null) return;
    updateMezzoTerrestre(mezzoTerrestreToPlaceId, { lat, lng });
    setMezzoTerrestreToPlaceId(null);
    setMezzoTerrestreMode(false);
    setShowSymbolSelector(true);
  }, [mezzoTerrestreToPlaceId, updateMezzoTerrestre]);

  const handleMoveMezzoTerrestre = useCallback((id: string, lat: number, lng: number) => {
    updateMezzoTerrestre(id, { lat, lng });
  }, [updateMezzoTerrestre]);

  const handleRimuoviMezzoDallaMappa = useCallback((id: string) => {
    updateMezzoTerrestre(id, { lat: undefined, lng: undefined });
  }, [updateMezzoTerrestre]);

  // ─────────────────────────────────────────────────────────
  // Handler posizione DOS
  // ─────────────────────────────────────────────────────────

  const handleAggiornaPosizioneDoS = useCallback(() => {
    if (!navigator.geolocation) return;
    setDosGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        aggiornaPosizioneDoS(pos.coords.latitude, pos.coords.longitude);
        setDosGpsLoading(false);
      },
      () => { setDosGpsLoading(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [aggiornaPosizioneDoS]);

  const handleMoveDoS = useCallback((lat: number, lng: number) => {
    aggiornaPosizioneDoS(lat, lng);
  }, [aggiornaPosizioneDoS]);

  const handleUpdateVedettaStato = useCallback(
    (id: number, statoOperativo: 'in_posizione' | 'non_risponde' | 'ritirata') => {
      updateLookout(id, { statoOperativo });
    },
    [updateLookout],
  );

  const handleUpdateVedettaAzimut = useCallback((id: number, settoreAzimut: number) => {
    updateLookout(id, { settoreAzimut });
  }, [updateLookout]);

  const handleUpdateVedettaAmpiezza = useCallback((id: number, settoreAmpiezza: number) => {
    updateLookout(id, { settoreAmpiezza });
  }, [updateLookout]);

  const handleConfirmCheckin = useCallback((id: number) => {
    updateLookout(id, { ultimoCheckin: new Date().toISOString() });
  }, [updateLookout]);

  const handleToggleTrigger = useCallback((id: number, trigger: string) => {
    const v = lookouts.find((l) => l.id === id);
    if (!v) return;
    const attivi = v.triggerAttivi ?? [];
    const next = attivi.includes(trigger)
      ? attivi.filter((t) => t !== trigger)
      : [...attivi, trigger];
    updateLookout(id, { triggerAttivi: next });
  }, [lookouts, updateLookout]);

  const handleSetInnescoByCoords = useCallback(() => {
    const input = prompt('Coordinate innesco (lat, lon):\nEs. 44.7847, 10.8791', '');
    if (!input) return;
    const parts = input.split(/[,\s]+/).map((s) => parseFloat(s.trim()));
    if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
      const [lat, lon] = parts;
      if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        setPuntoInnesco([lat, lon]);
        setCenterStore([lat, lon]);
        setZoomStore(15);
      } else {
        alert('Coordinate fuori range.\nLat: -90 / +90  —  Lon: -180 / +180');
      }
    } else {
      alert('Formato non valido.\nInserire: lat, lon   (es. 44.7847, 10.8791)');
    }
  }, [setCenterStore, setZoomStore]);

  // ─────────────────────────────────────────────────────────
  // GPS survey
  // ─────────────────────────────────────────────────────────

  const handleStartGpsSurvey = useCallback(() => {
    if (!navigator.geolocation) { alert('GPS non disponibile'); return; }
    if (gpsWatchRef.current !== null) { navigator.geolocation.clearWatch(gpsWatchRef.current); gpsWatchRef.current = null; }

    setGpsSurveyActive(true);
    setPerimetroMode(true);
    setShowPerimetroStats(true);
    setPerimetroChiuso(false);
    setPendingVertexIndex(null);
    setShowSymbolSelector(false);
    setShowMobileSymbols(false);
    setPlacementMode({ active: false, symbol: null });
    setPuntiPerimetro([]);
    lastPointRef.current = null;

    gpsWatchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        if (lastPointRef.current) {
          const [plat, plng] = lastPointRef.current;
          const dist = Math.sqrt(Math.pow(lat - plat, 2) + Math.pow(lng - plng, 2)) * 111000;
          if (dist < 8) return;
        }
        lastPointRef.current = [lat, lng];
        setPuntiPerimetro((prev) => [...prev, [lat, lng]]);
      },
      (err) => { console.error('Errore GPS:', err); alert('Errore acquisizione GPS'); },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );
  }, []);

  const handleStopGpsSurvey = useCallback(() => {
    if (gpsWatchRef.current !== null) { navigator.geolocation.clearWatch(gpsWatchRef.current); gpsWatchRef.current = null; }
    setGpsSurveyActive(false);
    lastPointRef.current = null;
    if (puntiPerimetro.length >= 3) { setPerimetroChiuso(true); setPerimetroMode(false); setShowSymbolSelector(true); }
  }, [puntiPerimetro.length]);

  useEffect(() => {
    return () => {
      if (gpsWatchRef.current !== null) { navigator.geolocation.clearWatch(gpsWatchRef.current); gpsWatchRef.current = null; }
    };
  }, []);

  // ─────────────────────────────────────────────────────────
  // Stampa / Export PDF
  // ─────────────────────────────────────────────────────────

  const handlePrint = useCallback(async () => {
    if (!mapAreaRef.current) return;

    setIsPrinting(true);
    // Attendi che React nasconda gli overlay prima di catturare
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    try {
      const canvas = await html2canvas(mapAreaRef.current, {
        useCORS: true,
        allowTaint: true,
        scale: 2,
        logging: false,
        backgroundColor: '#1e293b',
      });

      const imgData = canvas.toDataURL('image/png');

      const layerLabel = EFFIS_LAYER_OPTIONS.find((o) => o.value === effisLayer)?.label ?? effisLayer;
      const now = new Date().toLocaleString('it-IT');
      const areaStr   = areaHa > 0 ? `${areaHa.toFixed(2)} ha` : '—';
      const perimStr  = perimetroKm > 0 ? `${(perimetroKm * 1000).toFixed(0)} m` : '—';
      const innescoStr = puntoInnesco ? `${puntoInnesco[0].toFixed(5)}, ${puntoInnesco[1].toFixed(5)}` : '—';

      const html = `<!DOCTYPE html><html lang="it"><head>
        <meta charset="UTF-8"/>
        <title>DOS518 — Perimetro Incendio</title>
        <style>
          @page { size: A4 landscape; margin: 10mm 12mm; }
          body { margin:0; font-family: Arial, sans-serif; font-size: 11px; color: #111; }
          .header { display:flex; justify-content:space-between; align-items:center;
                    border-bottom: 2px solid #ff4500; padding-bottom:5px; margin-bottom:8px; }
          .title { font-size:14px; font-weight:800; color:#ff4500; }
          .subtitle { font-size:10px; color:#555; margin-top:2px; }
          .map-img { width:100%; border:1px solid #ccc; display:block; }
          .info { display:grid; grid-template-columns:repeat(4,1fr); gap:4px 16px;
                  border-top:2px solid #ff4500; padding-top:6px; margin-top:8px; }
          .cell span { display:block; color:#888; font-size:9px; }
          .cell strong { font-size:11px; }
          .notes { margin-top:6px; border-top:1px solid #ddd; padding-top:4px;
                   font-size:10px; color:#333; }
          .footer { margin-top:8px; display:flex; justify-content:space-between;
                    font-size:9px; color:#888; border-top:1px solid #eee; padding-top:4px; }
        </style>
      </head><body>
        <div class="header">
          <div>
            <div class="title">DOS518 — SI.TA.C. Perimetro Incendio</div>
            <div class="subtitle">Stampato il: ${now}</div>
          </div>
          <div style="font-size:10px;color:#555;">Evento: ${eventId ?? '—'}</div>
        </div>
        <img class="map-img" src="${imgData}" />
        <div class="info">
          <div class="cell"><span>Area bruciata</span><strong>${areaStr}</strong></div>
          <div class="cell"><span>Perimetro</span><strong>${perimStr}</strong></div>
          <div class="cell"><span>Stato</span><strong>${perimetroChiuso ? '✔ Chiuso' : '⚠ Aperto'}</strong></div>
          <div class="cell"><span>Punto innesco</span><strong>${innescoStr}</strong></div>
          <div class="cell"><span>Vento da</span><strong>${windDescription} ${Math.round(windSpeedKmh)} km/h (${windDirectionDeg}°)</strong></div>
          <div class="cell"><span>Propagazione verso</span><strong>${propagationLabel} (${propagationDeg}°)</strong></div>
          <div class="cell"><span>Combustibile</span><strong>${tipoVegetazione || '—'}</strong></div>
          <div class="cell"><span>Layer EFFIS</span><strong>${showEFFIS ? layerLabel : 'Non attivo'}</strong></div>
        </div>
        ${noteOperative ? `<div class="notes"><b>Note operative:</b> ${noteOperative}</div>` : ''}
        <div class="footer">
          <span>DOS518 © IRTILAC — Angelo Marino</span>
          <span>DOS: ${eventId ?? '—'}</span>
        </div>
        <script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); };<\/script>
      </body></html>`;

      const win = window.open('', '_blank', 'width=900,height=700');
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } catch (err) {
      console.error('Errore cattura mappa:', err);
      // Fallback: stampa CSS base
      window.print();
    } finally {
      setIsPrinting(false);
    }
  }, [
    areaHa, perimetroKm, perimetroChiuso, puntoInnesco,
    windDescription, windSpeedKmh, windDirectionDeg,
    propagationLabel, propagationDeg, tipoVegetazione,
    showEFFIS, effisLayer, noteOperative, eventId,
  ]);

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>

      {/* Input file nascosto per importazione GeoJSON */}
      <input
        ref={importFileRef}
        type="file"
        accept=".geojson,.json"
        style={{ display: 'none' }}
        onChange={handleImportFileChange}
      />

      {/* ── Toolbar con swipe per nascondere/mostrare ── */}
      <div style={{ overflow: 'hidden', maxHeight: toolbarVisible ? '160px' : '0', transition: 'max-height 0.3s ease' }}
        onTouchStart={(e) => { toolbarTouchStartY.current = e.touches[0].clientY; }}
        onTouchEnd={(e) => { if (e.changedTouches[0].clientY - toolbarTouchStartY.current < -35) closeAllMenus(); }}
      >
      <TacticalToolbar
        perimetroMode={perimetroMode}
        innescoMode={innescoMode}
        puntoInnescoPresente={puntoInnesco !== null}
        mapLayer={mapLayer}
        placedSymbolsCount={placedSymbols.length}
        showMobileSymbols={showMobileSymbols}
        showSymbolSelector={showSymbolSelector}
        readOnly={readOnly}
        showLegend={showLegend}
        onToggleLegend={() => setShowLegend((p) => !p)}
        onToggleMobileSymbols={() => {
          const next = !showSymbolSelector;
          setShowSymbolSelector(next);
          setShowMobileSymbols(next);
        }}
        onTogglePerimetroMode={handleTogglePerimetroMode}
        onToggleInnescoMode={handleToggleInnescoMode}
        onResetInnesco={handleResetInnesco}
        onSetInnescoByCoords={handleSetInnescoByCoords}
        onSetMapLayer={setMapLayer}
        showEFFIS={showEFFIS}
        effisLayer={effisLayer}
        onToggleEFFIS={() => setShowEFFIS((p) => !p)}
        onSetEffisLayer={setEffisLayer}
        onImportGeoJSON={handleImportGeoJSON}
        onPrint={handlePrint}
        onExportMap={handleExportMap}
        onClearMap={handleClearMap}
        geoStatus={geoStatus}
        onSearch={onSearch}
        isSearching={isSearching}
        onGPS={onGPS}
        resourceCounts={resourceCounts}
        showElettrodotti={elettrodottiVisible}
        statoElettrodotti={statoElec}
        pericoloAereo={pericoloAereo}
        onToggleElettrodotti={() => setElettrodottiVisible((v) => !v)}
        onRicaricaElettrodotti={ricaricaElec}
        onOpenOfflinePanel={() => setShowOfflinePanel((v) => !v)}
        vedettaMode={vedettaMode}
        vedettaCount={lookouts.length}
        vedetteDaPositionare={lookouts
          .filter((l) => l.lat === undefined)
          .map((l) => ({ id: l.id, nominativo: l.nominativo }))}
        onToggleVedettaMode={handleToggleVedettaMode}
        onSelectVedettaToPlace={handleSelectVedettaToPlace}
        mezzoTerrestreMode={mezzoTerrestreMode}
        mezziTerrestriCount={mezziTerrestri.length}
        mezziDaPositionare={mezziTerrestri
          .filter((m) => m.lat === undefined)
          .map((m) => ({ id: m.id, sigla: m.sigla, tipo: m.tipo }))}
        onToggleMezzoTerrestreMode={handleToggleMezzoTerrestreMode}
        onSelectMezzoToPlace={handleSelectMezzoToPlace}
        haPosizioneDoS={dosLat !== null && dosLng !== null}
        dosGpsLoading={dosGpsLoading}
        onAggiornaPosizioneDoS={handleAggiornaPosizioneDoS}
        showPosizioniCampo={showPosizioniCampo}
        campConnesso={campConnesso}
        campoAbilitato={!!codiceOperazione}
        posizioniCampoCount={posizioniCampo.length}
        onTogglePosizioniCampo={() => {
          setShowPosizioniCampo((v) => !v);
          onTogglePosizioniCampo?.();
        }}
      />
      </div>

      {/* ── Banner posizionamento simbolo / innesco ── */}
      <PlacementBanner
        placementMode={placementMode}
        innescoMode={innescoMode}
        onCancel={() => setPlacementMode({ active: false, symbol: null })}
        onCancelInnesco={() => { setInnescoMode(false); setShowSymbolSelector(true); }}
      />

      {/* ── Banner posizionamento vedetta ── */}
      {vedettaMode && vedettaToPlaceId !== null && (() => {
        const v = lookouts.find((l) => l.id === vedettaToPlaceId);
        return (
          <div style={{
            background: 'rgba(5,150,105,0.92)', color: '#fff',
            padding: '7px 14px', fontSize: '0.82rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            zIndex: 1200,
          }}>
            <span>🔭 Tocca la mappa per posizionare: <strong>{v?.nominativo}</strong></span>
            <button
              onClick={() => { setVedettaToPlaceId(null); setVedettaMode(false); setShowSymbolSelector(true); }}
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontWeight: 700 }}
            >✕</button>
          </div>
        );
      })()}

      {/* ── Banner posizionamento mezzo terrestre ── */}
      {mezzoTerrestreMode && mezzoTerrestreToPlaceId !== null && (() => {
        const m = mezziTerrestri.find((x) => x.id === mezzoTerrestreToPlaceId);
        return (
          <div style={{
            background: 'rgba(180,83,9,0.92)', color: '#fff',
            padding: '7px 14px', fontSize: '0.82rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            zIndex: 1200,
          }}>
            <span>🚒 Tocca la mappa per posizionare: <strong>{m?.sigla}</strong></span>
            <button
              onClick={() => { setMezzoTerrestreToPlaceId(null); setMezzoTerrestreMode(false); setShowSymbolSelector(true); }}
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontWeight: 700 }}
            >✕</button>
          </div>
        );
      })()}

      {/* ── Banner check-in scaduto ── */}
      {overdueIds.size > 0 && !isPrinting && (
        <div style={{
          background: '#dc2626', color: '#fff',
          padding: '6px 14px', fontSize: '0.82rem', fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 10, zIndex: 1200,
        }}>
          <span style={{ fontSize: '1rem' }}>🚨</span>
          <span>
            CHECK-IN SCADUTO:{' '}
            {lookouts
              .filter((l) => overdueIds.has(l.id))
              .map((l) => l.nominativo)
              .join(' · ')}
            {' '}— contattare via radio e premere ✅ Confermato
          </span>
        </div>
      )}

      {/* ── Pannello legenda (dropdown dalla toolbar) ── */}
      {showLegend && (
        <div style={{
          position: 'relative', zIndex: 1250,
          background: 'rgba(17,24,39,0.98)',
          borderBottom: '1px solid rgba(255,255,255,0.10)',
          padding: '12px 16px',
          maxHeight: '55vh', overflowY: 'auto',
        }}>
          <LegendPanel mapLayer={mapLayer} embedded />
        </div>
      )}

      {/* ── Pannello download mappe offline ── */}
      {showOfflinePanel && (
        <div style={{
          position: 'absolute', top: 54, right: 10, width: 290,
          background: 'rgba(15,23,42,0.98)',
          border: '1px solid rgba(16,185,129,0.4)',
          borderRadius: 10, padding: 16, zIndex: 1400,
          color: '#f1f5f9', boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#6ee7b7' }}>📥 Mappe Offline</span>
            <button
              onClick={() => setShowOfflinePanel(false)}
              style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer', padding: '2px 6px', minWidth: 32, minHeight: 32, touchAction: 'manipulation' }}
            >✕</button>
          </div>

          {/* Layer */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: 4 }}>Layer da scaricare</label>
            <select
              value={offlineLayer}
              onChange={(e) => setOfflineLayer(e.target.value)}
              disabled={isDownloading}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: '#1e293b', color: '#f1f5f9', fontSize: '0.82rem' }}
            >
              <option value="street">🗺️ Strade (OpenStreetMap)</option>
              <option value="opentopomap">🏔️ OpenTopoMap (sentieri)</option>
              <option value="topo">⛰️ Topo Esri</option>
              <option value="satellite">🛰️ Satellite Esri</option>
            </select>
          </div>

          {/* Raggio */}
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: 4 }}>Raggio dall'area visibile</label>
            <select
              value={offlineRadius}
              onChange={(e) => setOfflineRadius(Number(e.target.value))}
              disabled={isDownloading}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: '#1e293b', color: '#f1f5f9', fontSize: '0.82rem' }}
            >
              <option value={3}>3 km — ~150 tile</option>
              <option value={5}>5 km — ~400 tile</option>
              <option value={10}>10 km — ~1.600 tile</option>
            </select>
          </div>

          {/* Zoom massimo */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: 4 }}>Dettaglio massimo</label>
            <select
              value={offlineZoomMax}
              onChange={(e) => setOfflineZoomMax(Number(e.target.value))}
              disabled={isDownloading}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: '#1e293b', color: '#f1f5f9', fontSize: '0.82rem' }}
            >
              <option value={14}>Zoom 14 — strade principali</option>
              <option value={15}>Zoom 15 — strade minori + sentieri</option>
              <option value={16}>Zoom 16 — dettaglio massimo (lento)</option>
            </select>
          </div>

          {/* Progress */}
          {offlineProgress && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 4 }}>
                {offlineProgress.done
                  ? `✅ Completato — ${offlineProgress.downloaded} tile scaricate, ${offlineProgress.failed} errori`
                  : `⬇️ ${offlineProgress.downloaded} / ${offlineProgress.total} tile (${offlineProgress.percent}%)`}
              </div>
              <div style={{ height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3, transition: 'width 0.3s',
                  background: offlineProgress.done ? '#10b981' : '#3b82f6',
                  width: `${offlineProgress.percent}%`,
                }} />
              </div>
            </div>
          )}

          {/* Azioni download */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {!isDownloading ? (
              <button
                onClick={handleStartDownload}
                style={{ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', background: '#059669', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', touchAction: 'manipulation' }}
              >⬇️ Scarica area visibile</button>
            ) : (
              <button
                onClick={handleCancelDownload}
                style={{ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', touchAction: 'manipulation' }}
              >✕ Annulla</button>
            )}
          </div>

          {/* Info cache */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12 }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 8 }}>
              Cache totale: <strong style={{ color: '#94a3b8' }}>{cacheInfo.tiles} tile · {cacheInfo.sizeMB} MB</strong>
            </div>
            <button
              onClick={handleClearCache}
              disabled={cacheInfo.tiles === 0 || isDownloading}
              style={{ width: '100%', padding: '6px 0', borderRadius: 6, border: '1px solid rgba(220,38,38,0.4)', background: 'rgba(220,38,38,0.1)', color: '#fca5a5', fontSize: '0.78rem', cursor: 'pointer', touchAction: 'manipulation' }}
            >🗑️ Svuota tutta la cache</button>
          </div>
        </div>
      )}

      <div className={styles.content}>

        {/* ── Sidebar simboli (desktop / mobile overlay) ── */}
        {showSymbolSelector && !readOnly && (
          <>
            <div className={styles.sidebarDesktop}>
              <SITACSymbolSelector onSymbolSelect={handleSymbolSelect} compact={true} />
            </div>

            {showMobileSymbols && (
              <div className={styles.sidebarMobileOverlay}>
                <div className={styles.sidebarMobile}>
                  <div className={styles.mobilePanelHeader}>
                    <strong>Simboli SITAC</strong>
                    <button className={styles.btnTool} onClick={() => setShowMobileSymbols(false)}>✖</button>
                  </div>
                  <SITACSymbolSelector
                    onSymbolSelect={(symbol) => { handleSymbolSelect(symbol); setShowMobileSymbols(false); }}
                    compact={true}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Area mappa ── */}
        <div
          ref={mapAreaRef}
          style={{
            flex: 1,
            height,
            position: 'relative',
            border: activeMapBorder,
            boxShadow: activeMapShadow,
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          {/* Badge modalità innesco */}
          <InnescoModeBadge visible={innescoMode} />

          {/* Badge modalità perimetro */}
          <PerimetroModeBadge visible={perimetroMode} />

          {/* Overlay stats + azioni perimetro */}
          <PerimetroStatsOverlay
            visible={showPerimetroStats && !isPrinting}
            onClose={() => setShowPerimetroStats(false)}
            puntiCount={puntiPerimetro.length}
            areaHa={areaHa}
            perimetroKm={perimetroKm}
            perimetroChiuso={perimetroChiuso}
            gpsSurveyActive={gpsSurveyActive}
            propagationLabel={propagationLabel}
            propagationDeg={propagationDeg}
            windDescription={windDescription}
            windSpeedKmh={windSpeedKmh}
            windDataSource={windDataSource}
            windSourceLabel={windSourceLabel}
            puntoInnesco={puntoInnesco}
            tipoVegetazione={tipoVegetazione}
            noteOperative={noteOperative}
            onTipoVegetazioneChange={setTipoVegetazione}
            onNoteOperativeChange={setNoteOperative}
            onStartGpsSurvey={handleStartGpsSurvey}
            onStopGpsSurvey={handleStopGpsSurvey}
            onChiudiPerimetro={handleChiudiPerimetro}
            onModificaPerimetro={handleModificaPerimetro}
            onExportPerimetro={handleExportPerimetro}
            onResetPerimetro={handleResetPerimetro}
            distanzaInnescoFronte={distanzaInnescoFronte}
            innescoFuoriPerimetro={innescoFuoriPerimetro}
          />

          {/* Preview simbolo al centro della mappa */}
          <CenterSymbolPreview
            visible={placementMode.active && !perimetroMode}
            symbol={placementMode.symbol}
          />

          {/* Mirino crosshair */}
          <CenterCrosshairOverlay
            visible={innescoMode || vedettaMode || mezzoTerrestreMode || perimetroMode || placementMode.active}
            color={innescoMode ? '#dc2626' : vedettaMode ? '#059669' : mezzoTerrestreMode ? '#b45309' : perimetroMode ? '#ff4500' : '#3b82f6'}
          />

          {/* Griglia */}
          <GridOverlay visible={showGrid} />

          {/* Badge hillshade */}
          <HillshadeBadge visible={mapLayer === 'hillshade'} />

          {/* Badge legenda EFFIS */}
          <EFFISLegendBadge
            visible={showEFFIS}
            layerLabel={EFFIS_LAYER_OPTIONS.find((o) => o.value === effisLayer)?.label}
            effisStatus={effisStatus}
            effisLayer={effisLayer}
            onSetEffisLayer={setEffisLayer}
          />
          {/* Badge pericolo elettrodotti AT/AAT */}
          {pericoloAereo && elettrodottiVisible && !isPrinting && (
            <div style={{
              position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
              zIndex: 1200, background: '#CC0000', color: '#fff',
              padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 700,
              pointerEvents: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
              whiteSpace: 'nowrap',
            }}>
              ⚡ Elettrodotti AT/AAT nell'area — verificare con ENAC prima del sorvolo
            </div>
          )}

          {!isPrinting && (
            <WindVectorOverlay
              directionDeg={windDirectionDeg}
              speedKmh={windSpeedKmh}
              visible={!perimetroMode && !placementMode.active}
            />
          )}

          {/* Pannello controllo vento */}
          {!isPrinting && (
            <WindOverlay
              directionDeg={windDirectionDeg}
              speedKmh={windSpeedKmh}
              dataSource={windDataSource}
              updatedAt={windUpdatedAt}
              status={windFetchStatus}
              sourceLabel={
                windDataSource === 'auto'
                  ? `Reale · ${windSourceLabel}`
                  : windDataSource === 'cached'
                    ? `Cache locale · ${windSourceLabel}`
                    : 'Manuale operatore'
              }
              canRefresh={!readOnly}
              editable={!readOnly}
              onRefresh={fetchWindFromApi}
              onSourceChange={handleWindSourceChange}
              onDirectionChange={(value) => {
                setWindDirectionDeg(value);
                setWindDataSource('manual');
                setWindSourceLabel('Operatore');
                setWindFetchStatus('idle');
                setWindUpdatedAt(new Date().toISOString());
              }}
              onSpeedChange={(value) => {
                setWindSpeedKmh(value);
                setWindDataSource('manual');
                setWindSourceLabel('Operatore');
                setWindFetchStatus('idle');
                setWindUpdatedAt(new Date().toISOString());
              }}
              stale={windDataStale}
              staleAfterMin={windStaleAfterMin}
              refreshIntervalMin={windRefreshIntervalMin}
              onRefreshIntervalChange={setWindRefreshIntervalMin}
              onStaleAfterChange={setWindStaleAfterMin}
              defaultOpen={true}
              position="top-right"
            />
          )}

          {/* ── MapContainer Leaflet ── */}
          <MapContainer
            center={centerStore}
            zoom={zoomStore}
            style={{
              width: '100%',
              height: '100%',
              cursor: innescoMode || vedettaMode || mezzoTerrestreMode || perimetroMode || placementMode.active ? 'crosshair' : 'grab',
            }}
            zoomControl={true}
            dragging={true}
            touchZoom={true}
            doubleClickZoom={!innescoMode && !perimetroMode && !placementMode.active}
            scrollWheelZoom={!innescoMode && !perimetroMode && !placementMode.active}
          >
            <MapInstanceBridge onMap={setMapInstance} />
            <BaseTileLayer mapLayer={mapLayer} />
            {/* EFFIS overlay: sopra tile base, sotto simboli e perimetro */}
            <EFFISLayer visible={showEFFIS} temporalLayer={effisLayer} onStatusChange={setEffisStatus} />
            <MapViewUpdater center={centerStore} zoom={zoomStore} layoutVersion={layoutVersion} />
            <MapStateSync />
            <MapClickHandler
              placementMode={placementMode}
              onPlaceSymbol={handlePlaceSymbol}
              perimetroMode={perimetroMode}
              onAddPuntoPerimetro={handleAddPuntoPerimetro}
              innescoMode={innescoMode}
              onSetInnesco={handleSetInnesco}
              vedettaMode={vedettaMode && vedettaToPlaceId !== null}
              onSetVedetta={handleSetVedettaPosition}
              mezzoTerrestreMode={mezzoTerrestreMode && mezzoTerrestreToPlaceId !== null}
              onSetMezzoTerrestre={handleSetMezzoTerrestrePosition}
            />
            <SITACSymbolsLayer
              placedSymbols={placedSymbols}
              createSITACIcon={createSITACIcon}
              setSelectedSymbolId={setSelectedSymbolId}
              onMoveSymbol={handleMoveSymbol}
              readOnly={readOnly}
              dragEnabled={!perimetroMode && !placementMode.active}
            />
            <PerimetroIncendioLayer
              puntiPerimetro={puntiPerimetro}
              perimetroMode={perimetroMode}
              perimetroChiuso={perimetroChiuso}
              pendingVertexIndex={pendingVertexIndex}
              puntoInnesco={puntoInnesco}
              onMoveVertex={handleMoveVertex}
              onInsertVertex={handleInsertVertex}
              onDeleteVertex={handleDeleteVertex}
              onMoveInnesco={handleMoveInnesco}
              activeFrontSegment={activeFrontSegment}
              shelteredRearSegment={shelteredRearSegment}
              rightFlankSegment={rightFlankSegment}
              leftFlankSegment={leftFlankSegment}
            />
            {/* WindAnalysisLayer: triangolo predittivo automatico
                Appare non appena c'è il punto d'innesco — non serve il perimetro.
                Deve stare DOPO PerimetroIncendioLayer per z-order corretto. */}
            <WindAnalysisLayer
              directionDeg={windDirectionDeg}
              speedKmh={windSpeedKmh}
              puntoInnesco={puntoInnesco}
              tipoVegetazione={tipoVegetazione || undefined}
            />
            <PosizioniCampoLayer
              posizioni={posizioniCampo}
              visible={showPosizioniCampo}
            />
            <VedetteLayer
              vedette={lookouts}
              overdueIds={overdueIds}
              onMoveVedetta={handleMoveVedetta}
              onUpdateStato={handleUpdateVedettaStato}
              onUpdateAzimut={handleUpdateVedettaAzimut}
              onUpdateAmpiezza={handleUpdateVedettaAmpiezza}
              onConfirmCheckin={handleConfirmCheckin}
              onToggleTrigger={handleToggleTrigger}
              onOpenConeSettings={(id) => setVedettaConeSettingsId(id)}
              readOnly={readOnly}
            />
            <MezziTerrestriLayer
              mezzi={mezziTerrestri}
              onMoveMezzo={handleMoveMezzoTerrestre}
              onRimuoviDallaMappa={handleRimuoviMezzoDallaMappa}
              readOnly={readOnly}
            />
            {dosLat !== null && dosLng !== null && !isNaN(dosLat) && !isNaN(dosLng) && (
              <DoSMarkerLayer
                lat={dosLat}
                lng={dosLng}
                timestamp={(posizioneDoS as any)?.timestamp}
                onAggiornaPosizione={handleAggiornaPosizioneDoS}
                onMoveDos={handleMoveDoS}
                gpsLoading={dosGpsLoading}
                readOnly={readOnly}
              />
            )}
          </MapContainer>

          {/* Modale cono vedetta — portal fuori da Leaflet per evitare conflitti touch */}
          {vedettaConeSettingsId !== null && (() => {
            const v = lookouts.find((l) => l.id === vedettaConeSettingsId);
            if (!v) return null;
            const color = v.statoOperativo
              ? ({ in_posizione: '#22c55e', non_risponde: '#eab308', ritirata: '#ef4444' }[v.statoOperativo] ?? '#6366f1')
              : '#6366f1';
            return (
              <VedettaConeModal
                vedetta={v}
                color={color}
                onUpdateAzimut={handleUpdateVedettaAzimut}
                onUpdateAmpiezza={handleUpdateVedettaAmpiezza}
                onClose={() => setVedettaConeSettingsId(null)}
              />
            );
          })()}

          {/* Layer elettrodotti: API Leaflet imperativa, fuori da MapContainer */}
          {mapInstance && (
            <ElettrodottiLayer
              map={mapInstance}
              data={dataElec}
              visible={elettrodottiVisible}
            />
          )}

        </div>

        {/* ── Pannello proprietà simbolo selezionato ── */}
        {selectedSymbol && !readOnly && (
          <div className={styles.propertiesPanel}>
            <h4>Proprietà Simbolo</h4>

            <div className={styles.propertyGroup}>
              <label>Simbolo</label>
              <p>{selectedSymbol.symbol.descrizione}</p>
            </div>

            <div className={styles.propertyGroup}>
              <label>Coordinate GPS</label>
              <p>Lat: {selectedSymbol.position.lat.toFixed(6)}<br />Lng: {selectedSymbol.position.lng.toFixed(6)}</p>
            </div>

            <div className={styles.propertyGroup}>
              <label>🔄 Rotazione: {selectedSymbol.rotation || 0}°</label>
              <input type="range" min="0" max="360" value={selectedSymbol.rotation || 0}
                onChange={(e) => handleRotationChange(selectedSymbol.id, parseInt(e.target.value, 10))}
                style={{ width: '100%', cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
                {[0, 90, 180, 270].map((deg) => (
                  <button key={deg} onClick={() => handleRotationChange(selectedSymbol.id, deg)}
                    style={{ flex: 1, padding: '4px', fontSize: '11px', cursor: 'pointer' }}
                  >{deg}°</button>
                ))}
              </div>
            </div>

            <div className={styles.propertyGroup}>
              <label>📏 Dimensione: {((selectedSymbol.scale || 1) * 100).toFixed(0)}%</label>
              <input type="range" min="0.5" max="3" step="0.1" value={selectedSymbol.scale || 1}
                onChange={(e) => handleScaleChange(selectedSymbol.id, parseFloat(e.target.value))}
                style={{ width: '100%', cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
                {[0.5, 1, 1.5, 2].map((v) => (
                  <button key={v} onClick={() => handleScaleChange(selectedSymbol.id, v)}
                    style={{ flex: 1, padding: '4px', fontSize: '11px', cursor: 'pointer' }}
                  >{v * 100}%</button>
                ))}
              </div>
            </div>

            <div className={styles.propertyGroup}>
              <label>Timestamp</label>
              <p>{selectedSymbol.timestamp.toLocaleString()}</p>
            </div>

            <button className={styles.btnDanger} onClick={() => handleDeleteSymbol(selectedSymbol.id)}>
              🗑️ Elimina Simbolo
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TacticalMap;
